<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2" v-model="selectedValue">
        <el-option key="quickView" :label="$t('views.common.listView.quickView')" value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
        </el-option>
        <el-option
          key="export"
          :label="$t('views.common.listView.selectedRows.export')"
          value="export"
        >
        </el-option>
      </el-select>

      <el-input
        :placeholder="$t('views.common.listView.searchRows')"
        class="w-full col-span-12 md:col-span-10"
        :prefix-icon="Search"
        v-model="searchTerm"
      />
    </div>

    <ag-grid-vue
      v-if="rowModelType"
      class="ag-theme-alpine mt-4 md:mt-0"
      :columnDefs="columnDefs"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      :defaultColDef="defaultColDef"
      @cell-double-clicked="onCellDoubleClicked"
      :rowData="rowData"
      :pagination="selectedValue == 'allItems'"
      :paginationAutoPageSize="selectedValue == 'allItems'"
      :onGridReady="onGridReady"
      :rowBuffer="rowBuffer"
      :rowModelType="rowModelType"
      :cacheBlockSize="cacheBlockSize"
      :cacheOverflowSize="cacheOverflowSize"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :infiniteInitialRowCount="infiniteInitialRowCount"
      :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  ModuleRegistry,
  ProcessCellForExportParams
} from 'ag-grid-community'

import {
  Transaction,
  TransactionsPaginated,
  transactionI18nMessages
} from '@/models/accounting/Transaction'
import { useI18n } from 'vue-i18n'
import { formatPostgresDate, parseFormat } from '@/utils/date'
import {formatDinero, formatMoney} from '@/utils/money'
import { Search } from '@element-plus/icons-vue'
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model'
import { onBeforeMount } from 'vue'
import { ref } from 'vue'

ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'transactions',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...transactionI18nMessages.en
        },
        ar: {
          ...transactionI18nMessages.ar
        }
      }
    })

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      t,
      Search,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Transaction[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: '',
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined
    }
  },

  components: {
    AgGridVue
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: this.t('transactionID'),
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        minWidth: 200
      },
      {
        headerName: this.t('orderID'),
        field: 'referenceableExternalID'
      },
      {
        headerName: this.t('models.common.date'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const tx = params.node?.data as Transaction
          if (!tx) return ''
          if (tx.createdAt) {
            const createdAt = new Date(tx.createdAt)
            return formatPostgresDate(String(createdAt), 'DD/MM/YYYY')
          }
          return ''
        }
      },
      {
        headerName: this.t('models.common.time'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const tx = params.node?.data as Transaction
          if (!tx) return ''
          if (tx.createdAt) {
            const createdAt = new Date(tx.createdAt)
            return formatPostgresDate(String(createdAt), 'hh:mm A')
          }
          return ''
        }
      },
      {
        headerName: this.t('type'),
        field: 'receiptType',
        valueFormatter: (params) => {
          if (!params.value) return ''
          const tx = params.node?.data as Transaction
          return this.t(tx.receiptType)
        },
        cellStyle: ({ value }) => {
          const base = { textAlign: 'center', backgroundColor: 'transparent', color: '#000' }

          if (value == 'sale') {
            base['backgroundColor'] = '#52C750'
          } else if (value == 'return') {
            base['backgroundColor'] = '#DB4141'
            base['color'] = '#fff'
          } else if (value == 'collective_payment') {
            base['backgroundColor'] = '#e0b7ff'
          } else {
            base['backgroundColor'] = '#E8FF5C'
          }

          return base
        }
      },
      {
        headerName: this.t('totalAmount'),
        field: 'amountAmount',
        valueGetter: (params) => {
          const tx = params.node?.data as Transaction
          if (!tx) return ''
          if (tx.receiptType == 'collective_payment') {
            let amount = 0;
            for (const child of tx.children) {
              amount = amount + child.amountAmount
            }
            return formatMoney(amount, "IQD")
          }

          return formatDinero(tx.amountDinero)
        }
      },
      {
        headerName: this.t('customer'),
        valueGetter: (params) => {
          if (!params.data) return ''
          const tx = params.node?.data as any
          if (tx.customerInvoice?.customer) {
            return `${tx.customerInvoice?.customer?.name}`
          }
          return ''
        }
      },
      {
        headerName: this.t('user'),
        field: 'user.fullName',
        minWidth: 200
      }
    ]
  },

  mounted() {
    this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'export') {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  },

  methods: {
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()
      try {
        const data = await this.$http.get<Transaction[]>(`/sales/orders/transactions?parent_id=${this.$route.params.transactionID}`)
        this.rowData = data.map((transaction) => Transaction.from(transaction))
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          let url = `/sales/orders/transactions?startRow=${params.startRow}&endRow=${params.endRow}&parent_id=${this.$route.params.transactionID}`
          if (params.sortModel.length) {
            url += `&colId=${params.sortModel[0].colId}&sort=${params.sortModel[0].sort}`
          }

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          const res = await this.$http.get<TransactionsPaginated>(url)
          this.rowData = res.data.map((transaction) => Transaction.from(transaction))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      }
    },

    processCellForExport(params: ProcessCellForExportParams) {
      if (params.column.getColDef().headerName === 'Transaction Amount') {
        return params.node?.data.amountDinero ? formatDinero(params.node?.data.amountDinero) : ''
      } else {
        //if the cell doesn't belong to any of the above don't change it
        return params.value
      }
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    onCellDoubleClicked(event: any) {
      if (event.data?.receiptType == 'collective_payment') {
        this.$router.push(
            this.$Route.SALES_TRANSACTIONS_RECEIPT.replace(':transactionID', event.data.id)
        )
        return
      }
      this.$router.push(
        this.$Route.SALES_TRANSACTIONS_RECEIPT.replace(':transactionID', event.data.id)
      )
    }
  }
})
</script>

<style></style>
